import { RootRoute, Route, Router, createBrowserHistory, useNavigate } from '@tanstack/react-router';

import AccountPage from './pages/account/page';
import AgentPage from './pages/platform/agent/page';
import App from './App';
import BitflipPage from './pages/bitflip/page';
import BlogPage from './pages/blog/page';
import BlogPostPageWrapper from './pages/blog/[slug]';
import CreateNewProjectPage from './pages/create-new-project/page';
import CreateOrganizationPage from './components/settings/CreateOrganization';
import EditorPage from './pages/editor/page';
import ExportAndSharePage from './pages/platform/export-and-share/page';
import ForClientFacingDashboardsPage from './pages/solutions/for-client-facing-dashboards/page';
import ForEngineersPage from './pages/solutions/for-engineers/page';
import ForEnterprisesPage from './pages/solutions/for-enterprises/page';
import ForExploratoryAnalysisPage from './pages/solutions/for-exploratory-analysis/page';
import ForGovernmentsPage from './pages/solutions/for-governments/page';
import ForInteractiveReportsPage from './pages/solutions/for-interactive-reports/page';
import ForInternalToolsPage from './pages/solutions/for-internal-tools/page';
import ForOpsAndAnalystsPage from './pages/solutions/for-ops-and-analysts/page';
import ForResearchersPage from './pages/solutions/for-researchers/page';
import ForStudentsPage from './pages/solutions/for-students/page';
import HackathonPage from './pages/hackathon/page';
import HackathonRegisterPage from './pages/hackathon/register/page';
import HackathonSubmitPage from './pages/hackathon/submit/page';
import LandingPage from './pages/landing/page';
import LivePreviewsPage from './pages/platform/live-previews/page';
import LoadingState from './components/ui/loading-state';
import OrganizationSettingsPage from './pages/settings/organization/page';
import { Outlet } from '@tanstack/react-router';
import PricingPage from './pages/pricing/page';
import PrivacyPolicyPage from './pages/privacy-policy/page';
import ProjectDetailsPage from './pages/project-details/page';
import ProjectsDashboardPage from './pages/platform/projects-dashboard/page';
import ProjectsPage from './pages/projects/page';
import React from 'react';
import SettingsPage from './pages/settings/page';
import SignIn from './components/auth/SignIn';
import SignInSSOCallbackPage from './pages/sign-in/sso-callback/page';
import SignUp from './components/auth/SignUp';
import SignUpSSOCallbackPage from './pages/sign-up/sso-callback/page';
import TeamCollaborationPage from './pages/platform/team-collaboration/page';
import TermsAndConditionsPage from './pages/terms-and-conditions/page';
import VisualEditorPage from './pages/platform/visual-editor/page';
import WaitForOrg from './components/auth/WaitForOrg';
import { rootStore } from './stores/RootStore';
import { useUser } from '@clerk/clerk-react';

// Define the root route
const rootRoute = new RootRoute({
    component: App,
});

// Home page route
const homeRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/home',
    component: () => (
        <LandingPage />
    ),
});

// default redirect route
const indexRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/',
    component: () => {
        const { isLoaded, isSignedIn } = useUser();
        const navigate = useNavigate();

        React.useEffect(() => {
            if (!isLoaded) return;

            if (!isSignedIn) {
                console.log('[Root Route] Unauthenticated – redirecting to /home');
                navigate({ to: '/home', replace: true });
            } else {
                console.log('[Root Route] Authenticated – redirecting to /dashboard');
                navigate({ to: '/dashboard', replace: true });
            }
        }, [isLoaded, isSignedIn, navigate]);

        return <LoadingState fullScreen message="Loading..." />;
    },
});

const pricingRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/pricing',
    component: PricingPage,
});

const privacyPolicyRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/privacy-policy',
    component: PrivacyPolicyPage,
});

const termsAndConditionsRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/terms-and-conditions',
    component: TermsAndConditionsPage,
});

const blogRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/blog',
    component: BlogPage,
});

const blogPostRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/blog/$slug',
    component: BlogPostPageWrapper,
});

const bitflipRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/bitflip',
    component: BitflipPage,
});

// Platform Section - Landing Page Routes
const visualEditorRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/platform/visual-editor',
    component: VisualEditorPage,
});

const agentRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/platform/agent',
    component: AgentPage,
});

const projectsDashboardRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/platform/projects-dashboard',
    component: ProjectsDashboardPage,
});

const livePreviewsRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/platform/live-previews',
    component: LivePreviewsPage,
});

const teamCollaborationRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/platform/team-collaboration',
    component: TeamCollaborationPage,
});

// Solutions Section - Landing Page Routes
const forEngineersRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/solutions/for-engineers',
    component: ForEngineersPage,
});

const forOpsAndAnalystsRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/solutions/for-ops-and-analysts',
    component: ForOpsAndAnalystsPage,
});

const forStudentsRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/solutions/for-students',
    component: ForStudentsPage,
});

const forResearchersRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/solutions/for-researchers',
    component: ForResearchersPage,
});

const forInternalToolsRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/solutions/for-internal-tools',
    component: ForInternalToolsPage,
});

const forInteractiveReportsRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/solutions/for-interactive-reports',
    component: ForInteractiveReportsPage,
});

const forExploratoryAnalysisRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/solutions/for-exploratory-analysis',
    component: ForExploratoryAnalysisPage,
});

const forClientFacingDashboardsRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/solutions/for-client-facing-dashboards',
    component: ForClientFacingDashboardsPage,
});

const forEnterprisesRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/solutions/for-enterprises',
    component: ForEnterprisesPage,
});

const forGovernmentsRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/solutions/for-governments',
    component: ForGovernmentsPage,
});

const exportAndShareRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/platform/export-and-share',
    component: ExportAndSharePage,
});

// Organization route - serves as parent for org-specific routes
const orgRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/org/$orgSlug',
});

// Projects page at /org/<org-slug>
const orgProjectsRoute = new Route({
    getParentRoute: () => orgRoute,
    path: '/',
    component: ProjectsPage,
});

// Project details page at /org/<org-slug>/project-details/<project-id>
const projectDetailsRoute = new Route({
    getParentRoute: () => orgRoute,
    path: '/project-details/$projectId',
    component: ProjectDetailsPage,
});

// Create new project page at /org/<org-slug>/create-new-project
const createNewProjectRoute = new Route({
    getParentRoute: () => orgRoute,
    path: '/create-new-project',
    component: CreateNewProjectPage,
});

// Editor page at /org/<org-slug>/<project-id>
const orgEditorRoute = new Route({
    getParentRoute: () => orgRoute,
    path: '/$projectId',
    component: EditorPage,
});

// Settings routes
const settingsRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/settings',
    component: SettingsPage,
});

const accountRoute = new Route({
    getParentRoute: () => settingsRoute,
    path: '/account',
    component: AccountPage,
});

const orgSettingsRoute = new Route({
    getParentRoute: () => settingsRoute,
    path: '/organization',
    component: OrganizationSettingsPage,
});

const createOrgRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/create-org',
    component: CreateOrganizationPage,
});

const waitForOrgRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/dashboard',
    component: WaitForOrg,
});

const signInRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/sign-in',
    component: SignIn,
});

const signInWildcardRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/sign-in/*',
    component: SignIn,
});

const signUpRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/sign-up',
    component: SignUp,
});

const signUpWildcardRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/sign-up/*',
    component: SignUp,
});

const verifyEmailRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/sign-up/verify-email-address',
    component: SignUp,
});

const hackathonRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/hackathon',
    component: HackathonPage,
});

const hackathonRegisterRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/hackathon/register',
    component: HackathonRegisterPage,
});

const hackathonSubmitRoute = new Route({
    getParentRoute: () => rootRoute,
    path: '/hackathon/submit',
    component: HackathonSubmitPage,
});

// Create the route tree using the routes
const routeTree = rootRoute.addChildren([
    indexRoute,
    homeRoute,
    pricingRoute,
    privacyPolicyRoute,
    termsAndConditionsRoute,
    blogRoute,
    blogPostRoute,
    bitflipRoute,
    hackathonRoute,
    hackathonRegisterRoute,
    hackathonSubmitRoute,
    waitForOrgRoute,
    signInRoute,
    signInWildcardRoute,
    signUpRoute,
    signUpWildcardRoute,
    verifyEmailRoute,
    // Platform Section - Landing Page Routes
    visualEditorRoute,
    agentRoute,
    projectsDashboardRoute,
    livePreviewsRoute,
    teamCollaborationRoute,
    exportAndShareRoute,
    // Solutions Section - Landing Page Routes
    forEngineersRoute,
    forOpsAndAnalystsRoute,
    forStudentsRoute,
    forResearchersRoute,
    forInternalToolsRoute,
    forInteractiveReportsRoute,
    forExploratoryAnalysisRoute,
    forClientFacingDashboardsRoute,
    forEnterprisesRoute,
    forGovernmentsRoute,
    settingsRoute.addChildren([
        orgSettingsRoute,
        accountRoute,
    ]),
    orgRoute.addChildren([
        orgProjectsRoute,
        createNewProjectRoute,
        projectDetailsRoute,
        orgEditorRoute,
    ]),
    createOrgRoute,
]);

// Create the router using the route tree
export const router = new Router({
    routeTree,
    history: createBrowserHistory(),
    context: {
        stores: rootStore,
    },
});

// Register the router for type safety
declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router;
    }
} 